import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '../../config/supabase';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';

// Détection de l'environnement
const isLocal =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1" ||
  window.location.hostname.endsWith(".app.github.dev");

// Fonction pour déterminer l'URL de l'API
const getApiUrl = () => {
  const baseUrl = import.meta.env.VITE_FUNCTION_URL;
  return `${baseUrl}/api/UpdateCommune`;
};
const apiUrl = getApiUrl();

const getApiUrlCentroide = () => {
  const baseUrl = import.meta.env.VITE_FUNCTION_URL;
  return `${baseUrl}/api/UpdateCommuneCentroide`;
};
const apiUrlCentroide = getApiUrlCentroide();

const getApiUrlImport = () => {
  const baseUrl = import.meta.env.VITE_FUNCTION_URL;
  return `${baseUrl}/api/ImportCommuneData`;
};
const apiUrlImport = getApiUrlImport();

const getApiUrlDelete = () => {
  const baseUrl = import.meta.env.VITE_FUNCTION_URL;
  return `${baseUrl}/api/DeleteCommune`;
};
const apiUrlDelete = getApiUrlDelete();

const getApiUrlIgnore = () => {
  const baseUrl = import.meta.env.VITE_FUNCTION_URL;
  return `${baseUrl}/api/IgnoreCommune`;
};
const apiUrlIgnore = getApiUrlIgnore();

const getApiUrlImportCentroide = () => {
  const baseUrl = import.meta.env.VITE_FUNCTION_URL;
  return `${baseUrl}/api/ImportCommuneCentroide`;
};
const apiUrlImportCentroide = getApiUrlImportCentroide();

/**
 * Page de mise à jour des communes (réservée aux administrateurs)
 */
const UpdateCommunePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [statsCommunes, setStatsCommunes] = useState(null);
  const [loadingStatsCommunes, setLoadingStatsCommunes] = useState(false);
  const [statsImportFile, setStatsImportFile] = useState(null);
  const [loadingStatsImportFile, setLoadingStatsImportFile] = useState(false);
  const [functionKey, setFunctionKey] = useState(null);
  const [keyLoading, setKeyLoading] = useState(true);
  const [keyError, setKeyError] = useState(null);
  const [functionKeyMissing, setFunctionKeyMissing] = useState(false);
  
  // États pour les centroïdes
  const [isExecutingCentroide, setIsExecutingCentroide] = useState(false);
  const [resultCentroide, setResultCentroide] = useState(null);
  
  // États pour l'import des données
  const [isExecutingImport, setIsExecutingImport] = useState(false);
  const [resultImport, setResultImport] = useState(null);
  
  // États pour la suppression des communes
  const [isExecutingDelete, setIsExecutingDelete] = useState(false);
  const [resultDelete, setResultDelete] = useState(null);
  
  // États pour l'identification des communes à ignorer
  const [isExecutingIgnore, setIsExecutingIgnore] = useState(false);
  const [resultIgnore, setResultIgnore] = useState(null);
  
  // États pour l'import des centroïdes
  const [isExecutingImportCentroide, setIsExecutingImportCentroide] = useState(false);
  const [resultImportCentroide, setResultImportCentroide] = useState(null);
  
  // États pour les statistiques communes_centroides
  const [statsCommunesCentroides, setStatsCommunesCentroides] = useState(null);
  const [loadingStatsCommunesCentroides, setLoadingStatsCommunesCentroides] = useState(false);

  // Récupération de la clé de fonction depuis Supabase (remplace useFunctionKey)
  useEffect(() => {
    const getFunctionKey = async () => {
      try {
        const keyName = isLocal ? 'FUNCTION_KEY_DEV' : 'FUNCTION_KEY';
        const { data, error } = await supabase
          .from('keys')
          .select('value')
          .eq('env', keyName)
          .single();
        if (error) {
          setKeyError(error.message);
          setFunctionKeyMissing(true);
        } else if (data) {
          setFunctionKey(data.value);
          setFunctionKeyMissing(false);
        } else {
          setFunctionKeyMissing(true);
        }
      } catch (err) {
        setKeyError(err.message);
        setFunctionKeyMissing(true);
      } finally {
        setKeyLoading(false);
      }
    };
    getFunctionKey();
  }, [isLocal]);

  /**
   * Récupère les statistiques détaillées de la table communes
   */
  const fetchStatsCommunes = async () => {
    try {
      setLoadingStatsCommunes(true);
      
      // Compter le total
      const { count: total, error: errorTotal } = await supabase
        .from('communes')
        .select('*', { count: 'exact', head: true });

      if (errorTotal) {
        console.error('Erreur:', errorTotal);
        toast.error('Erreur lors de la récupération des stats communes');
        return;
      }

      // Compter les combinaisons distinctes (code_commune_insee + code_postal)
      // Pagination pour récupérer toutes les lignes (limite Supabase = 1000 par requête)
      let uniqueCombinationsCount = 0;
      const uniqueSet = new Set();
      const pageSize = 1000;
      let currentPage = 0;
      let hasMore = true;

      while (hasMore) {
        const from = currentPage * pageSize;
        const to = from + pageSize - 1;

        const { data: combinaisons, error: errorCombinaisons } = await supabase
          .from('communes')
          .select('code_commune_insee, code_postal')
          .range(from, to);

        if (errorCombinaisons) {
          console.error('Erreur combinaisons:', errorCombinaisons);
          hasMore = false;
          break;
        }

        if (!combinaisons || combinaisons.length === 0) {
          hasMore = false;
          break;
        }

        // Ajouter les combinaisons au Set
        combinaisons.forEach(row => {
          uniqueSet.add(`${row.code_commune_insee || 'NULL'}|${row.code_postal || 'NULL'}`);
        });

        // Si on a reçu moins que pageSize, c'est la dernière page
        if (combinaisons.length < pageSize) {
          hasMore = false;
        } else {
          currentPage++;
        }
      }

      uniqueCombinationsCount = uniqueSet.size;

      // Compter les champs complétés pour chaque colonne
      const fields = [
        'code_commune_insee',
        'nom_de_la_commune',
        'code_postal',
        'libelle_acheminement',
        'autre',
        'coordonnees_gps',
        'latitude',
        'longitude',
        'include',
        'freshness',
        'geom',
        'duplicate',
        'delete',
        'ignore'
      ];

      const fieldStats = {};
      
      for (const field of fields) {
        let query = supabase
          .from('communes')
          .select('*', { count: 'exact', head: true });
        
        // Pour delete, duplicate et ignore, compter uniquement les valeurs TRUE
        if (field === 'delete' || field === 'duplicate' || field === 'ignore') {
          query = query.eq(field, true);
        } else {
          query = query.not(field, 'is', null);
        }
        
        const { count, error } = await query;
        
        if (!error) {
          fieldStats[field] = count || 0;
        }
      }

      setStatsCommunes({
        total: total || 0,
        uniqueCombinations: uniqueCombinationsCount,
        fields: fieldStats
      });
      
    } catch (error) {
      console.error('Erreur inattendue:', error);
      toast.error('Erreur lors de la récupération des stats communes');
    } finally {
      setLoadingStatsCommunes(false);
    }
  };

  /**
   * Récupère les statistiques détaillées de la table communes_importfile
   */
  const fetchStatsImportFile = async () => {
    try {
      setLoadingStatsImportFile(true);
      
      // Compter le total
      const { count: total, error: errorTotal } = await supabase
        .from('communes_importfile')
        .select('*', { count: 'exact', head: true });

      if (errorTotal) {
        console.error('Erreur:', errorTotal);
        toast.error('Erreur lors de la récupération des stats import file');
        return;
      }

      // Compter les champs complétés pour chaque colonne
      const fields = [
        '#Code_commune_INSEE',
        'Nom_de_la_commune',
        'Code_postal',
        'Libellé_d_acheminement',
        'Ligne_5',
        '_contours_commune.geometry',
        'geom'
      ];

      const fieldStats = {};
      
      for (const field of fields) {
        // Échapper les noms de colonnes contenant des caractères spéciaux
        // Les champs commençant par # ou contenant . doivent être entre guillemets
        const fieldName = (field.startsWith('#') || field.includes('.')) ? `"${field}"` : field;
        
        const { count, error } = await supabase
          .from('communes_importfile')
          .select('*', { count: 'exact', head: true })
          .not(fieldName, 'is', null);
        
        if (!error) {
          fieldStats[field] = count || 0;
        } else {
          console.warn(`Erreur pour le champ ${field}:`, error);
        }
      }

      setStatsImportFile({
        total: total || 0,
        fields: fieldStats
      });
      
    } catch (error) {
      console.error('Erreur inattendue:', error);
      toast.error('Erreur lors de la récupération des stats import file');
    } finally {
      setLoadingStatsImportFile(false);
    }
  };

  /**
   * Récupère les statistiques détaillées de la table communes_centroides
   */
  const fetchStatsCommunesCentroides = async () => {
    try {
      setLoadingStatsCommunesCentroides(true);
      
      // Compter le total
      const { count: total, error: errorTotal } = await supabase
        .from('communes_centroides')
        .select('*', { count: 'exact', head: true });

      if (errorTotal) {
        console.error('Erreur:', errorTotal);
        toast.error('Erreur lors de la récupération des stats communes_centroides');
        return;
      }

      // Compter les champs complétés pour chaque colonne
      const fields = [
        'code_commune_insee',
        'nom_de_la_commune',
        'code_postal',
        'libelle_d_acheminement',
        'ligne_5',
        '_geopoint',
        'latitude',
        'longitude'
      ];

      const fieldStats = {};
      
      for (const field of fields) {
        const { count, error } = await supabase
          .from('communes_centroides')
          .select('*', { count: 'exact', head: true })
          .not(field, 'is', null);
        
        if (!error) {
          fieldStats[field] = count || 0;
        } else {
          console.warn(`Erreur pour le champ ${field}:`, error);
        }
      }

      setStatsCommunesCentroides({
        total: total || 0,
        fields: fieldStats
      });
      
    } catch (error) {
      console.error('Erreur inattendue:', error);
      toast.error('Erreur lors de la récupération des stats communes_centroides');
    } finally {
      setLoadingStatsCommunesCentroides(false);
    }
  };

  /**   * Exécute l'API UpdateCommune
   */
  const handleExecute = async () => {
    if (!functionKey) {
      toast.error('Clé de fonction manquante');
      return;
    }

    try {
      setIsExecuting(true);
      setResult(null);
      
      // Appel à l'API locale UpdateCommune
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-functions-key': functionKey
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage = errorData?.message || `Erreur HTTP: ${response.status}`;
        console.error('Erreur lors de l\'exécution:', errorMessage);
        toast.error(t('admin.updateCommune.executionError'));
        return;
      }

      const data = await response.json();
      console.log('Résultat:', data);
      
      if (data.status === 'success') {
        setResult(data.stats);
        toast.success(t('admin.updateCommune.executionSuccess'));
        // Recharger les stats après l'exécution
        fetchStatsCommunes();
      } else {
        toast.error(data.message || t('admin.updateCommune.executionError'));
      }
      
    } catch (error) {
      console.error('Erreur inattendue:', error);
      toast.error(t('admin.updateCommune.executionError'));
    } finally {
      setIsExecuting(false);
    }
  };

  /**
   * Exécute l'API ImportCommuneData
   */
  const handleExecuteImport = async () => {
    if (!functionKey) {
      toast.error('Clé de fonction manquante');
      return;
    }

    try {
      setIsExecutingImport(true);
      setResultImport(null);
      
      // Appel à l'API ImportCommuneData
      const response = await fetch(apiUrlImport, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-functions-key': functionKey
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage = errorData?.message || `Erreur HTTP: ${response.status}`;
        console.error('Erreur lors de l\'import:', errorMessage);
        toast.error('Erreur lors de l\'import des données communales');
        return;
      }

      const data = await response.json();
      console.log('Résultat import:', data);
      
      if (data.status === 'success') {
        setResultImport(data.stats);
        toast.success('Import des données communales terminé avec succès');
        // Recharger les stats après l'exécution
        fetchStatsCommunes();
        fetchStatsImportFile();
      } else {
        toast.error(data.message || 'Erreur lors de l\'import des données communales');
      }
      
    } catch (error) {
      console.error('Erreur inattendue import:', error);
      toast.error('Erreur lors de l\'import des données communales');
    } finally {
      setIsExecutingImport(false);
    }
  };

  /**
   * Exécute l'API DeleteCommune
   */
  const handleExecuteDelete = async () => {
    if (!functionKey) {
      toast.error('Clé de fonction manquante');
      return;
    }

    try {
      setIsExecutingDelete(true);
      setResultDelete(null);
      
      // Appel à l'API DeleteCommune
      const response = await fetch(apiUrlDelete, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-functions-key': functionKey
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage = errorData?.message || `Erreur HTTP: ${response.status}`;
        console.error('Erreur lors de l\'identification des communes supprimées:', errorMessage);
        toast.error('Erreur lors de l\'identification des communes supprimées');
        return;
      }

      const data = await response.json();
      console.log('Résultat identification suppression:', data);
      
      if (data.status === 'success') {
        setResultDelete(data.stats);
        toast.success('Identification des communes supprimées terminée avec succès');
        // Recharger les stats après l'exécution
        fetchStatsCommunes();
      } else {
        toast.error(data.message || 'Erreur lors de l\'identification des communes supprimées');
      }
      
    } catch (error) {
      console.error('Erreur inattendue identification suppression:', error);
      toast.error('Erreur lors de l\'identification des communes supprimées');
    } finally {
      setIsExecutingDelete(false);
    }
  };

  /**
   * Exécute l'API IgnoreCommune
   */
  const handleExecuteIgnore = async () => {
    if (!functionKey) {
      setResultIgnore({ error: 'Clé de fonction manquante' });
      return;
    }

    try {
      setIsExecutingIgnore(true);
      setResultIgnore(null);
      
      const response = await fetch(apiUrlIgnore, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-functions-key': functionKey
        }
      });
      
      const data = await response.json();
      setResultIgnore(data);
    } catch (error) {
      console.error('Erreur lors de l\'identification des communes à ignorer:', error);
      setResultIgnore({ error: error.message });
    } finally {
      setIsExecutingIgnore(false);
    }
  };

  /**
   * Exécute l'API ImportCommuneCentroide
   */
  const handleExecuteImportCentroide = async () => {
    if (!functionKey) {
      toast.error('Clé de fonction manquante');
      return;
    }

    try {
      setIsExecutingImportCentroide(true);
      setResultImportCentroide(null);
      
      // Appel à l'API ImportCommuneCentroide
      const response = await fetch(apiUrlImportCentroide, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-functions-key': functionKey
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage = errorData?.message || `Erreur HTTP: ${response.status}`;
        console.error('Erreur lors de l\'import centroïde:', errorMessage);
        toast.error('Erreur lors de l\'import des données centroïdes');
        return;
      }

      const data = await response.json();
      console.log('Résultat import centroïde:', data);
      
      if (data.status === 'success') {
        setResultImportCentroide(data.stats);
        toast.success('Import des données centroïdes terminé avec succès');
        // Recharger les stats après l'exécution
        fetchStatsCommunes();
      } else {
        toast.error(data.message || 'Erreur lors de l\'import des données centroïdes');
      }
      
    } catch (error) {
      console.error('Erreur inattendue import centroïde:', error);
      toast.error('Erreur lors de l\'import des données centroïdes');
    } finally {
      setIsExecutingImportCentroide(false);
    }
  };

  /**
   * Exécute l'API UpdateCommuneCentroide
   */
  const handleExecuteCentroide = async () => {
    if (!functionKey) {
      toast.error('Clé de fonction manquante');
      return;
    }

    try {
      setIsExecutingCentroide(true);
      setResultCentroide(null);
      
      // Appel à l'API locale UpdateCommuneCentroide
      const response = await fetch(apiUrlCentroide, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-functions-key': functionKey
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage = errorData?.message || `Erreur HTTP: ${response.status}`;
        console.error('Erreur lors de l\'exécution centroïde:', errorMessage);
        toast.error('Erreur lors de la mise à jour des centroïdes');
        return;
      }

      const data = await response.json();
      console.log('Résultat centroïde:', data);
      
      if (data.status === 'success') {
        setResultCentroide(data.stats);
        toast.success('Mise à jour des centroïdes terminée avec succès');
        // Recharger les stats après l'exécution
        fetchStatsCommunes();
      } else {
        toast.error(data.message || 'Erreur lors de la mise à jour des centroïdes');
      }
      
    } catch (error) {
      console.error('Erreur inattendue centroïde:', error);
      toast.error('Erreur lors de la mise à jour des centroïdes');
    } finally {
      setIsExecutingCentroide(false);
    }
  };

  // Affichage du spinner pendant la vérification admin
  if (adminLoading || keyLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // Si l'utilisateur n'est pas admin, le hook le redirige automatiquement
  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gris-clair">
      <PageHeader 
        title={t('settings.admin.updateCommune.title')} 
        backPath="/settings"
      />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Avertissement si la clé de fonction est manquante */}
          {functionKeyMissing && (
            <div className="mb-4 p-3 rounded-lg bg-yellow-100 text-yellow-800 border border-yellow-300">
              <div className="flex items-start gap-2">
                <span className="text-lg">⚠️</span>
                <div>
                  <p className="font-medium">Configuration requise</p>
                  <p className="text-sm mt-1">
                    {keyError || 'La clé de fonction n\'a pas pu être récupérée depuis la base de données.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Import Fichier Data Commune */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-h2 text-bleu2 mb-4">Import Fichier Data Commune</h3>
            <p className="text-body text-gray-700 mb-4">
              Télécharger le fichier{' '}
              <a 
                href="https://datanova.laposte.fr/datasets/laposte-hexasmal" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-bleu1 hover:underline"
              >
                https://datanova.laposte.fr/datasets/laposte-hexasmal
              </a>
              {' '}puis ajouter au Storage Supabase
            </p>
            <button
              onClick={handleExecuteImport}
              disabled={isExecutingImport || functionKeyMissing}
              className={`w-full py-3 px-6 rounded-lg font-medium text-white transition-all duration-200 ${
                isExecutingImport || functionKeyMissing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-bleu1 hover:bg-bleu3 active:scale-[0.98]'
              }`}
            >
              {isExecutingImport ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Import en cours...
                </span>
              ) : (
                'Lancer l\'import des données'
              )}
            </button>
            
            {resultImport && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <span className="font-medium">✓ Import réussi</span>
                  {resultImport.filesProcessed !== undefined && ` - ${resultImport.filesProcessed} fichier(s) traité(s)`}
                  {resultImport.totalLines !== undefined && ` - ${resultImport.totalLines} ligne(s) lue(s)`}
                  {resultImport.inserted !== undefined && ` - ${resultImport.inserted} insertion(s)`}
                  {resultImport.updated !== undefined && ` - ${resultImport.updated} mise(s) à jour`}
                </p>
              </div>
            )}
          </div>
{/* Statistiques de la table communes_importfile */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-h2 text-bleu2">Table communes_importfile</h3>
              <button
                onClick={fetchStatsImportFile}
                disabled={loadingStatsImportFile}
                className={`py-2 px-4 rounded-lg font-medium text-white transition-all duration-200 ${
                  loadingStatsImportFile
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-bleu1 hover:bg-bleu3 active:scale-[0.98]'
                }`}
              >
                {loadingStatsImportFile ? 'Chargement...' : 'Actualiser'}
              </button>
            </div>
            
            {statsImportFile && (
              <div className="space-y-2">
                <div className="flex justify-between items-center py-2 px-3 bg-bleu2 text-white rounded-t-lg font-medium">
                  <span className="text-body">Champ</span>
                  <span className="text-body">Complétés / Total</span>
                </div>
                <div className="flex justify-between items-center py-2 px-3 bg-gray-50 border-b border-gray-200">
                  <span className="text-body text-gray-700 font-bold">TOTAL</span>
                  <span className="text-body font-bold text-bleu2">{statsImportFile.total.toLocaleString()}</span>
                </div>
                {Object.entries(statsImportFile.fields).map(([field, count]) => (
                  <div key={field} className="flex justify-between items-center py-2 px-3 border-b border-gray-100">
                    <span className="text-body text-gray-700">{field}</span>
                    <span className="text-body text-gray-900">
                      {count.toLocaleString()} / {statsImportFile.total.toLocaleString()}
                      <span className="ml-2 text-xs text-gray-500">
                        ({statsImportFile.total > 0 ? ((count / statsImportFile.total) * 100).toFixed(1) : 0}%)
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {!statsImportFile && !loadingStatsImportFile && (
              <p className="text-body text-gray-500 text-center py-4">
                Cliquez sur "Actualiser" pour afficher les statistiques
              </p>
            )}
          </div>
          
          {/* Statistiques de la table communes */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-h2 text-bleu2">Table communes</h3>
              <button
                onClick={fetchStatsCommunes}
                disabled={loadingStatsCommunes}
                className={`py-2 px-4 rounded-lg font-medium text-white transition-all duration-200 ${
                  loadingStatsCommunes
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-bleu1 hover:bg-bleu3 active:scale-[0.98]'
                }`}
              >
                {loadingStatsCommunes ? 'Chargement...' : 'Actualiser'}
              </button>
            </div>
            
            {statsCommunes && (
              <div className="space-y-2">
                <div className="flex justify-between items-center py-2 px-3 bg-bleu2 text-white rounded-t-lg font-medium">
                  <span className="text-body">Champ</span>
                  <span className="text-body">Complétés / Total</span>
                </div>
                <div className="flex justify-between items-center py-2 px-3 bg-gray-50 border-b border-gray-200">
                  <span className="text-body text-gray-700 font-bold">TOTAL</span>
                  <span className="text-body font-bold text-bleu2">{statsCommunes.total.toLocaleString()}</span>
                </div>
                {Object.entries(statsCommunes.fields).map(([field, count]) => (
                  <div key={field} className="flex justify-between items-center py-2 px-3 border-b border-gray-100">
                    <span className="text-body text-gray-700">{field}</span>
                    <span className="text-body text-gray-900">
                      {count.toLocaleString()} / {statsCommunes.total.toLocaleString()}
                      <span className="ml-2 text-xs text-gray-500">
                        ({statsCommunes.total > 0 ? ((count / statsCommunes.total) * 100).toFixed(1) : 0}%)
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {!statsCommunes && !loadingStatsCommunes && (
              <p className="text-body text-gray-500 text-center py-4">
                Cliquez sur "Actualiser" pour afficher les statistiques
              </p>
            )}
          </div>

          

          {/* Bouton d'exécution - UpdateCommune */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-h3 text-gray-700 mb-3 font-medium">Mise à jour communes depuis communes_importfile</h3>
            <p className="text-sm text-gray-600 mb-4">
              Lance la fonction UpdateCommune pour mettre à jour les communes depuis la table communes_importfile
            </p>
            <button
              onClick={handleExecute}
              disabled={isExecuting || functionKeyMissing}
              className={`w-full py-3 px-6 rounded-lg font-medium text-white transition-all duration-200 ${
                isExecuting || functionKeyMissing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-bleu1 hover:bg-bleu3 active:scale-[0.98]'
              }`}
            >
              {isExecuting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Mise à jour en cours...
                </span>
              ) : (
                'Lancer UpdateCommune'
              )}
            </button>
            
            {result && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <span className="font-medium">✓ Mise à jour réussie</span>
                  {result.filesProcessed !== undefined && ` - ${result.filesProcessed} fichier(s) traité(s)`}
                  {result.totalLines !== undefined && ` - ${result.totalLines} ligne(s) lue(s)`}
                  {result.inserted !== undefined && ` - ${result.inserted} insertion(s)`}
                  {result.updated !== undefined && ` - ${result.updated} mise(s) à jour`}
                </p>
              </div>
            )}
          </div>

          {/* Bouton d'exécution - DeleteCommune */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-h3 text-gray-700 mb-3 font-medium">Identification des communes supprimées</h3>
            <p className="text-sm text-gray-600 mb-4">
              Lance la fonction DeleteCommune pour identifier les communes qui n'existent plus dans communes_importfile
            </p>
            <button
              onClick={handleExecuteDelete}
              disabled={isExecutingDelete || functionKeyMissing}
              className={`w-full py-3 px-6 rounded-lg font-medium text-white transition-all duration-200 ${
                isExecutingDelete || functionKeyMissing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700 active:scale-[0.98]'
              }`}
            >
              {isExecutingDelete ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Identification en cours...
                </span>
              ) : (
                'Lancer DeleteCommune'
              )}
            </button>
            
            {resultDelete && (
              <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800">
                  <span className="font-medium">✓ Identification terminée</span>
                  {resultDelete.communesToDelete !== undefined && ` - ${resultDelete.communesToDelete} commune(s) identifiée(s)`}
                  {resultDelete.deleted && ` - ${resultDelete.deleted} supprimée(s)`}
                </p>
              </div>
            )}
          </div>

          {/* Bouton d'exécution - IgnoreCommune */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-h3 text-gray-700 mb-3 font-medium">Identification des communes à ignorer</h3>
            <p className="text-sm text-gray-600 mb-4">
              Lance la fonction IgnoreCommune pour identifier les communes qui doivent être ignorées
            </p>
            <button
              onClick={handleExecuteIgnore}
              disabled={isExecutingIgnore || functionKeyMissing}
              className={`w-full py-3 px-6 rounded-lg font-medium text-white transition-all duration-200 ${
                isExecutingIgnore || functionKeyMissing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-orange-600 hover:bg-orange-700 active:scale-[0.98]'
              }`}
            >
              {isExecutingIgnore ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Identification en cours...
                </span>
              ) : (
                'Lancer IgnoreCommune'
              )}
            </button>
            
            {resultIgnore && (
              <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-800">
                  <span className="font-medium">✓ Identification terminée</span>
                  {resultIgnore.communesToIgnore !== undefined && ` - ${resultIgnore.communesToIgnore} commune(s) identifiée(s)`}
                  {resultIgnore.ignored && ` - ${resultIgnore.ignored} marquée(s) à ignorer`}
                </p>
              </div>
            )}
          </div>

          {/* Import Fichier Data Commune Centroide */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-h2 text-bleu2 mb-4">Import Fichier Data Commune Centroide</h3>
            <p className="text-body text-gray-700 mb-4">
              Télécharger le fichier depuis{' '}
              <a 
                href="https://datanova.laposte.fr/data-fair/api/v1/datasets/laposte-hexasmal/metadata-attachments/base-officielle-codes-postaux.csv" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-bleu1 hover:underline"
              >
                base-officielle-codes-postaux.csv
              </a>
              {' '}puis ajouter au Storage Supabase
            </p>
            <button
              onClick={handleExecuteImportCentroide}
              disabled={isExecutingImportCentroide || functionKeyMissing}
              className={`w-full py-3 px-6 rounded-lg font-medium text-white transition-all duration-200 ${
                isExecutingImportCentroide || functionKeyMissing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-bleu1 hover:bg-bleu3 active:scale-[0.98]'
              }`}
            >
              {isExecutingImportCentroide ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Import en cours...
                </span>
              ) : (
                'Lancer l\'import des centroïdes'
              )}
            </button>
            
            {resultImportCentroide && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <span className="font-medium">✓ Import réussi</span>
                  {resultImportCentroide.filesProcessed !== undefined && ` - ${resultImportCentroide.filesProcessed} fichier(s) traité(s)`}
                  {resultImportCentroide.totalLines !== undefined && ` - ${resultImportCentroide.totalLines} ligne(s) lue(s)`}
                  {resultImportCentroide.inserted !== undefined && ` - ${resultImportCentroide.inserted} insertion(s)`}
                  {resultImportCentroide.updated !== undefined && ` - ${resultImportCentroide.updated} mise(s) à jour`}
                </p>
              </div>
            )}
          </div>

          {/* Bouton d'exécution - UpdateCommuneCentroide */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-h3 text-gray-700 mb-3 font-medium">Mise à jour des centroïdes (coordonnées GPS)</h3>
            <button
              onClick={handleExecuteCentroide}
              disabled={isExecutingCentroide || functionKeyMissing}
              className={`w-full py-3 px-6 rounded-lg font-medium text-white transition-all duration-200 ${
                isExecutingCentroide || functionKeyMissing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-bleu1 hover:bg-bleu3 active:scale-[0.98]'
              }`}
            >
              {isExecutingCentroide ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Mise à jour des centroïdes en cours...
                </span>
              ) : (
                'Lancer UpdateCommuneCentroide'
              )}
            </button>
            
            {resultCentroide && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <span className="font-medium">✓ Mise à jour réussie</span>
                  {resultCentroide.updated !== undefined && ` - ${resultCentroide.updated} centroïde(s) mis à jour`}
                </p>
              </div>
            )}
          </div>

          {/* Résultats */}
          {/* {result && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-h2 mb-4 text-bleu2">{t('admin.updateCommune.results')}</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-body text-gray-600">{t('admin.updateCommune.filesProcessed')}</span>
                  <span className="font-medium text-bleu2">{result.filesProcessed}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-body text-gray-600">{t('admin.updateCommune.totalLines')}</span>
                  <span className="font-medium text-bleu2">{result.totalLines}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-body text-gray-600">{t('admin.updateCommune.updated')}</span>
                  <span className="font-medium text-green-600">{result.updated}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-body text-gray-600">{t('admin.updateCommune.inserted')}</span>
                  <span className="font-medium text-blue-600">{result.inserted}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-body text-gray-600">{t('admin.updateCommune.skipped')}</span>
                  <span className="font-medium text-gray-500">{result.skipped}</span>
                </div>
                {result.testMode && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <span className="font-medium">⚠️ {t('admin.updateCommune.testMode')}</span>
                      {result.testCodePostal && ` - ${t('admin.updateCommune.testCodePostal')}: ${result.testCodePostal}`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )} */}
          
          {/* Statistiques de la table communes_centroides */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-h2 text-bleu2">Table communes_centroides</h3>
              <button
                onClick={fetchStatsCommunesCentroides}
                disabled={loadingStatsCommunesCentroides}
                className={`py-2 px-4 rounded-lg font-medium text-white transition-all duration-200 ${
                  loadingStatsCommunesCentroides
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-bleu1 hover:bg-bleu3 active:scale-[0.98]'
                }`}
              >
                {loadingStatsCommunesCentroides ? 'Chargement...' : 'Actualiser'}
              </button>
            </div>
            
            {statsCommunesCentroides && (
              <div className="space-y-2">
                <div className="flex justify-between items-center py-2 px-3 bg-bleu2 text-white rounded-t-lg font-medium">
                  <span className="text-body">Champ</span>
                  <span className="text-body">Complétés / Total</span>
                </div>
                <div className="flex justify-between items-center py-2 px-3 bg-gray-50 border-b border-gray-200">
                  <span className="text-body text-gray-700 font-bold">TOTAL</span>
                  <span className="text-body font-bold text-bleu2">{statsCommunesCentroides.total.toLocaleString()}</span>
                </div>
                {Object.entries(statsCommunesCentroides.fields).map(([field, count]) => (
                  <div key={field} className="flex justify-between items-center py-2 px-3 border-b border-gray-100">
                    <span className="text-body text-gray-700">{field}</span>
                    <span className="text-body text-gray-900">
                      {count.toLocaleString()} / {statsCommunesCentroides.total.toLocaleString()}
                      <span className="ml-2 text-xs text-gray-500">
                        ({statsCommunesCentroides.total > 0 ? ((count / statsCommunesCentroides.total) * 100).toFixed(1) : 0}%)
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {!statsCommunesCentroides && !loadingStatsCommunesCentroides && (
              <p className="text-body text-gray-500 text-center py-4">
                Cliquez sur "Actualiser" pour afficher les statistiques
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateCommunePage;
